import { isAwaitingExtraction } from '@/features/deal-review/deal-lead-views'
import {
  directionLabel,
  fieldLabel,
  tagLabel,
} from '@/features/deal-review/deal-lead-labels'
import type { DealLeadDetail } from '@/types/api'

export interface OverviewField {
  evidenceKey: string
  label: string
  value: string
  isAbsent: boolean
  derived: boolean
}

export interface OverviewSection {
  title: string
  fields: OverviewField[]
}

export const ABSENT_VALUE_LABEL = 'Not stated'

function money(value: number | undefined): string | null {
  if (value === undefined) return null
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}M`
}

// Stored as ratios: 0.8 is 80%.
export function ratio(value: number | undefined): string | null {
  if (value === undefined) return null
  return `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`
}

function multiple(value: number | undefined): string | null {
  if (value === undefined) return null
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}x`
}

function text(value: string | undefined): string | null {
  if (value === undefined) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function date(value: string | undefined): string | null {
  if (value === undefined) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// An empty list means unclassified, never 'nothing excluded', so it reads as absent.
function tags(value: string[]): string | null {
  if (value.length === 0) return null
  return value.map(tagLabel).join(', ')
}

function field(
  evidenceKey: string,
  formatted: string | null,
  derived = false
): OverviewField {
  return {
    evidenceKey,
    label: fieldLabel(evidenceKey),
    value: formatted ?? ABSENT_VALUE_LABEL,
    isAbsent: formatted === null,
    derived,
  }
}

const TAG_AXES = ['sectorTags', 'businessModelTags', 'transactionTags']

// The mapper writes the three tag axes' shared quote under `tags`.
export function evidenceKeyFor(field: string): string {
  return TAG_AXES.includes(field) ? 'tags' : field
}

function tagAxis(axis: string, formatted: string | null): OverviewField {
  return { ...field(evidenceKeyFor(axis), formatted), label: fieldLabel(axis) }
}

export function overviewSections(lead: DealLeadDetail): OverviewSection[] {
  return [
    {
      title: 'Business',
      fields: [
        field('companyName', text(lead.companyName)),
        field('projectName', text(lead.projectName)),
        field('sector', text(lead.sector)),
        field('subSector', text(lead.subSector)),
        field('geography', text(lead.geography)),
        field('country', text(lead.country)),
        field('description', text(lead.description)),
      ],
    },
    {
      title: 'Reported figures',
      fields: [
        field('revenue', money(lead.revenue)),
        field('revenueGrowthPct', ratio(lead.revenueGrowthPct)),
        field('ebitda', money(lead.ebitda)),
        field(
          'ebitdaMarginPct',
          ratio(lead.ebitdaMarginPct),
          lead.ebitdaMarginDerived
        ),
        field('grossMarginPct', ratio(lead.grossMarginPct)),
        field('netProfit', money(lead.netProfit)),
        field('askingPrice', money(lead.askingPrice)),
        field('valuationMultiple', multiple(lead.valuationMultiple)),
      ],
    },
    {
      title: 'Concentration',
      fields: [
        field('largestCustomerPct', ratio(lead.largestCustomerPct)),
        field(
          'stateRevenueConcentrationPct',
          ratio(lead.stateRevenueConcentrationPct)
        ),
        field('rolloverEquityPct', ratio(lead.rolloverEquityPct)),
      ],
    },
    {
      title: 'Transaction',
      fields: [
        field(
          'direction',
          lead.direction ? directionLabel(lead.direction) : null
        ),
        field('transactionType', text(lead.transactionType)),
        field('fundingType', text(lead.fundingType)),
        field('reasonForSale', text(lead.reasonForSale)),
        field('timingNote', text(lead.timingNote)),
        // No extraction key produces this, so no quote renders; don't reuse timingNote's.
        field('timingDeadline', date(lead.timingDeadline)),
      ],
    },
    {
      title: 'Source',
      fields: [
        field('brokerCompany', text(lead.brokerCompany)),
        field('brokerName', text(lead.brokerName)),
        field('brokerEmail', text(lead.brokerEmail)),
      ],
    },
    {
      title: 'Classification',
      fields: [
        tagAxis('sectorTags', tags(lead.sectorTags)),
        tagAxis('businessModelTags', tags(lead.businessModelTags)),
        tagAxis('transactionTags', tags(lead.transactionTags)),
      ],
    },
  ]
}

export function displayValue(
  lead: DealLeadDetail,
  evidenceKey: string
): string | null {
  const found = overviewSections(lead)
    .flatMap((section) => section.fields)
    .find((field) => field.evidenceKey === evidenceKey)
  return found && !found.isAbsent ? found.value : null
}

export function evidenceQuote(
  lead: DealLeadDetail,
  evidenceKey: string
): string | null {
  const evidence = readEvidence(lead)
  if (!evidence) return null

  const quote = evidence[evidenceKey]
  return typeof quote === 'string' && quote.trim().length > 0
    ? quote.trim()
    : null
}

export type EvidenceAbsence = 'NOT_EXTRACTED' | 'NO_QUOTES_RECORDED' | null

export function evidenceAbsence(lead: DealLeadDetail): EvidenceAbsence {
  // Extraction runs are message-scoped, so a run's presence says nothing about this candidate.
  if (isAwaitingExtraction(lead)) return 'NOT_EXTRACTED'
  if (!lead.latestExtractionRun) return 'NOT_EXTRACTED'

  const evidence = readEvidence(lead)
  const hasQuote =
    evidence !== undefined &&
    Object.values(evidence).some(
      (quote) => typeof quote === 'string' && quote.trim().length > 0
    )

  return hasQuote ? null : 'NO_QUOTES_RECORDED'
}

// Generated type is `Record<string, never>`; widened to unknown and checked per value.
function readEvidence(
  lead: DealLeadDetail
): Record<string, unknown> | undefined {
  return lead.latestExtractionRun?.evidence
}

export function formatMillions(value: number | undefined): string {
  if (value === undefined) return ''
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 1 })}M`
}

export function formatReceivedAt(value: string): string {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
